/*eslint-disable @typescript-eslint/explicit-module-boundary-types*/
import {ErrorCodeStatus, OnboardingStatus, OnboardingStep} from "@/app/utils/common/constants";
import {callbackApplicationAjax, isNullOrEmpty} from "@/app/utils/common/functions/functions";
import { AdyenErrorCodeMapper } from "@/app/utils/common/functions/payfac-error-code-mapper/adyen/error-code-mapper";
import { FinixErrorCodeMapper } from "@/app/utils/common/functions/payfac-error-code-mapper/finix/error-code-mapper";
import { copyFromApiResponse } from "@/store/storeMappers";
import Vue from "vue";

export const determineOnboardingStatus = (provisioningStatus: string | undefined,
                                          failureReasonCode: string | undefined,
                                          payfac: string | undefined) => {
  let status = provisioningStatus ?? OnboardingStatus.NOT_INITIATED;

  if (provisioningStatus === OnboardingStatus.REJECTED) {
    const errorCodeMapper = (payfac === "FINIX") ? new FinixErrorCodeMapper() : new AdyenErrorCodeMapper();
    status = failureReasonCode?.split(",").some(x => errorCodeMapper
        .cannotOnboardErrorCodes()
        .includes(errorCodeMapper.getRawErrorCode(x))) ?
        OnboardingStatus.REJECTED_WITH_DECLINE :
        OnboardingStatus.REJECTED;
  }
  
  return {
    status, failureCodes: failureReasonCode?.split(",").map(x => x.trim()).filter(x => x) ?? [],
  }
}

export const payfacDetailsDefaultState = () => ({
  errorCodes: {},
  data: {
    provisioningStatus: {
      apiPath: "provisioning_status",
      value: OnboardingStatus.NOT_INITIATED
    },
    onboardingStep: {
      apiPath: "onboarding_step",
      value: OnboardingStep.NOT_INITIATED
    },
    failureReasonCode: {
      apiPath: "failure_reason_code",
      value: '',
    },
    failureReasonCategory: {
      apiPath : "failure_reason_category",
      value: ''
    },
    updatedAt: {
      apiPath : "updated_at",
      value: null
    },
  }
});


export const payfacDetailsState = {
  ADYEN : payfacDetailsDefaultState(),
  FINIX:  payfacDetailsDefaultState()
}

const payfacDetailsGetters = {
  /* To get onboarding status as per the payfac, Payfac parameter has to be passed.
   Why cannot we use sessionPayfac ? Because, merchant are supposed to onboard into both payfac,
   one will be active payfac, while other will be used during migration. */
  getOnboardingStatus: state => (payfac) => state[payfac].data.provisioningStatus.value,
  getOnboardingStep: state => (payfac) => state[payfac].data.onboardingStep.value,
  getFailureReasonCategory: state => (payfac) => state[payfac].data.failureReasonCategory.value,
  getUpdatedAt: state => (payfac) => state[payfac].data.updatedAt.value,
  isEligibleForMigration: (state, getters, rootState, rootGetters) => {
    /* Not Eligible if active Payfac is Adyen */
    if (rootGetters['app/getActivePayfac'] === "ADYEN") {
      return false;
    }
    // Eligible if active payfac and sessionPayfac are different and  if merchant is not Approved on the migrated payfac
    return (rootGetters['app/getActivePayfac'] !== rootGetters['app/getSessionPayfac'])
        && state["ADYEN"].data.provisioningStatus.value !== "APPROVED";
  },

  /* All the below getters are using sessionPayfac because they will be used during onboarding and migration only. */
  getErrorCodesToBeFixed: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    return isNullOrEmpty(errorCodes) ? [] : Object.keys(errorCodes)
        .filter(errorCode => errorCodes[errorCode] === ErrorCodeStatus.NOT_FIXED);
  },
  getErrorCodes: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    return isNullOrEmpty(errorCodes) ? [] : Object.keys(errorCodes);
  },
  containsErrorCodeRequiringFileUpload: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    return isNullOrEmpty(errorCodes) ? false : Object.keys(errorCodes).some(e => rootGetters["app/getPayfacErrorCodeMapper"]
        .errorCodesRequiringFileUpload().includes(rootGetters['app/getPayfacErrorCodeMapper'].getRawErrorCode(e)));
  },
  getDocumentUploadErrorCodes: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    if (isNullOrEmpty(errorCodes)) return [];

    const mapper = rootGetters["app/getPayfacErrorCodeMapper"];
    const seen = new Set();
    return Object.keys(errorCodes).filter(code => {
      const raw = mapper.getRawErrorCode(code);
      return mapper.errorCodesRequiringFileUpload().includes(raw) && !seen.has(raw) && seen.add(raw);
    });
  },
  containsBankRelatedErrors: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    return isNullOrEmpty(errorCodes) ? false : Object.keys(errorCodes)
        .some(x => rootGetters["app/getPayfacErrorCodeMapper"]
            .rejectionDueToBankAccount()
            .includes(rootGetters["app/getPayfacErrorCodeMapper"]
                .getRawErrorCode(x)))
  },
  getBankRelatedErrorCodes: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    if (isNullOrEmpty(errorCodes)) return [];

    const mapper = rootGetters["app/getPayfacErrorCodeMapper"];
    const seen = new Set();
    return Object.keys(errorCodes).filter(code => {
      const raw = mapper.getRawErrorCode(code);
      return mapper.rejectionDueToBankAccount().includes(raw) && !seen.has(raw) && seen.add(raw);
    });
  },
  getBusinessProfileErrorCodes: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    if (isNullOrEmpty(errorCodes)) return [];

    const mapper = rootGetters["app/getPayfacErrorCodeMapper"];
    const seen = new Set();
    return Object.keys(errorCodes).filter(code => {
      const raw = mapper.getRawErrorCode(code);
      return mapper.rejectionDueToBusinessStructure().includes(raw) && !seen.has(raw) && seen.add(raw);
    });
  },
  getControlPersonErrorCodes: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    if (isNullOrEmpty(errorCodes)) return [];

    const mapper = rootGetters["app/getPayfacErrorCodeMapper"];
    const seen = new Set();
    return Object.keys(errorCodes).filter(code => {
      const raw = mapper.getRawErrorCode(code);
      return mapper.rejectionDueToOwnerInfo().includes(raw) && !seen.has(raw) && seen.add(raw);
    });
  },
  containsBusinessRegistrationErrors: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    return isNullOrEmpty(errorCodes) ? false : Object.keys(errorCodes)
      .some(x => rootGetters["app/getPayfacErrorCodeMapper"]
        .rejectionDueToBusinessInformation()
        .includes(rootGetters["app/getPayfacErrorCodeMapper"]
          .getRawErrorCode(x)))
  },
  getBusinessRegistrationErrorCodes: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    if (isNullOrEmpty(errorCodes)) return [];

    const mapper = rootGetters["app/getPayfacErrorCodeMapper"];
    const seen = new Set();
    return Object.keys(errorCodes).filter(code => {
      const raw = mapper.getRawErrorCode(code);
      return mapper.rejectionDueToBusinessInformation().includes(raw) && !seen.has(raw) && seen.add(raw);
    });
  },

  containsDeclinedErrorCode: (state, getters, rootState, rootGetters) => {
    const errorCodes = state[rootGetters['app/getSessionPayfac']].errorCodes;
    return isNullOrEmpty(errorCodes) ? false : Object.keys(errorCodes)
        .some(x => rootGetters["app/getPayfacErrorCodeMapper"]
            .cannotOnboardErrorCodes()
            .includes(rootGetters["app/getPayfacErrorCodeMapper"]
                .getRawErrorCode(x)))
  }
}

const actions = {
  copyFromMerchantApiResponse({commit}, merchantApiResponse) {
    commit('copyFromApiResponse', merchantApiResponse)
  },
  setErrorCodeFixed({commit, rootGetters}, merchantErrorCode) {
    commit('setErrorCodeFixed', {"payfac" : rootGetters['app/getSessionPayfac'],
      "value" :merchantErrorCode});
  },
  setErrorCodes({commit, rootGetters}, merchantErrorCode) {
    commit('setErrorCodes', {"payfac" : rootGetters['app/getSessionPayfac'],
      "value" :merchantErrorCode});
  },
  setOnboardingStatus({commit}, {payfac, provisioningStatus}) {
    commit('setOnboardingStatus', {"payfac": payfac, "status": provisioningStatus})
  },
  setValuesFromIncrementalOnboardingResponse({commit, rootGetters}, response) {
    commit('setOnboardingStatus', {"payfac": rootGetters['app/getSessionPayfac'], "status": response.provisioning_status})
    commit('setOnboardingStep', {"payfac": rootGetters['app/getSessionPayfac'], "step": response.onboarding_step})
    rootGetters['app/getCallbackUrl'] && callbackApplicationAjax(rootGetters['app/getCallbackUrl'], response.id, null, response.onboarding_step);
  }
}

const mutations = {
  copyFromApiResponse: function (state, merchantApiResponse):void {
    const payfacDetails = merchantApiResponse?.payfac_details;
    if (isNullOrEmpty(payfacDetails) || payfacDetails.length === 0) return;
    
    payfacDetails.forEach((payfacDetail) => {
      const newPayfacDetail = payfacDetailsDefaultState();
      copyFromApiResponse(payfacDetail, newPayfacDetail.data);

      const failureCodes = newPayfacDetail.data.failureReasonCode.value?.split(",").map(x => x.trim()).filter(x => x) ?? []

      failureCodes.forEach(v => {
        Vue.set(newPayfacDetail.errorCodes, v, ErrorCodeStatus.NOT_FIXED)
      });
      state[payfacDetail.payfac] = newPayfacDetail;
    })
  },
  setErrorCodeFixed: function(state, {payfac, value}): void {
    (value || []).forEach(v => {
      Vue.set(state[payfac].errorCodes, v, ErrorCodeStatus.FIXED)
    });
  },
  setErrorCodes: function(state, {payfac, value}): void{
    (value || []).forEach(v => {
      Vue.set(state[payfac].errorCodes, v, ErrorCodeStatus.NOT_FIXED)
    });
  },
  setOnboardingStatus: function (state, {payfac, status}) : void {
    state[payfac].data.provisioningStatus.value = status;
  },
  setOnboardingStep: function (state, {payfac, step}) : void {
    state[payfac].data.onboardingStep.value = step;
  }
}

export const merchantPayfacDetails = {
  namespaced: true,
  state: payfacDetailsState,
  getters: payfacDetailsGetters,
  actions,
  mutations,
};
